import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useIsArtefactEditable } from '@modules/common/hooks';
import {
  getDashboardRoute,
  getEmojiFromDashboard,
} from '@modules/dashboard/helpers';
import { RemoveCircleOutlineRounded } from '@mui/icons-material';
import { Box, Button, Paper, Stack, Tooltip } from '@mui/material';
import { DashboardListItemViewFragment, Status } from 'generated/graphql';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardAdditionalDetails } from '../DashboardAdditionalDetails';
import { RemoveAsTemplateDialogContent } from '../RemoveAsTemplateDialogContent';

interface DashboardTemplateListItemProps {
  template: DashboardListItemViewFragment;
}

export const DashboardTemplateListItem = ({
  template,
}: DashboardTemplateListItemProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const { openModal } = useMuiModalContext();

  const editToken = template.editToken;

  const removeAsTemplate = () => {
    const dashboardWithEditToken = {
      ...template,
      editToken: editToken ?? '',
    };
    openModal({
      contentComponent: () => (
        <RemoveAsTemplateDialogContent dashboard={dashboardWithEditToken} />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  const templateRoute = getDashboardRoute(template);

  const isEditable = useIsArtefactEditable({
    editToken: template.editToken,
    status: undefined,
  });

  const renderRemoveAsTemplateButton = () => (
    <Button
      disabled={!isEditable}
      variant="text"
      startIcon={<RemoveCircleOutlineRounded />}
      onClick={removeAsTemplate}
    >
      Remove as Template
    </Button>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '4px',
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Stack>
          <Link to={templateRoute} target="_blank">
            {getEmojiFromDashboard(template)} {template.title}
          </Link>
          <DashboardAdditionalDetails
            dashboard={{
              ...template,
              status: Status.Unarchived,
            }}
          />
        </Stack>
        {isHovering &&
          (editToken ? (
            renderRemoveAsTemplateButton()
          ) : (
            <Tooltip
              title="You don't have edit access to remove this template"
              placement="top"
            >
              <Box>{renderRemoveAsTemplateButton()}</Box>
            </Tooltip>
          ))}
      </Stack>
    </Paper>
  );
};
